import { Edit2, Eye, Plus, Puzzle, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import { useApps } from '../contexts/AppsContext';
import componentsService from '../services/components';
import rolesService from '../services/roles';
import { splitRoles } from '../utils/roleFlags';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';

const ComponentsPage = () => {
    const { apps, loading: loadingApps, error: appsError } = useApps();
    const [appId, setAppId] = useState('');
    const [components, setComponents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchParams] = useSearchParams();

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showComponentRolesModal, setShowComponentRolesModal] = useState(false);

    // Selection
    const [selectedComponent, setSelectedComponent] = useState(null);

    // Role Mapping State
    const [componentRoles, setComponentRoles] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [loadingComponentRoles, setLoadingComponentRoles] = useState(false);
    const [mappingError, setMappingError] = useState('');

    // Add/Edit Mapping
    const [newMapping, setNewMapping] = useState({ rolename: '', access: '{"access": "readonly"}' });
    const [editingRoleMappingId, setEditingRoleMappingId] = useState(null);
    const [editAccessValue, setEditAccessValue] = useState('');

    const [formData, setFormData] = useState({
        component_name: '',
        component_description: '',
    });
    const [updateFormData, setUpdateFormData] = useState({
        old_component_name: '',
        new_component_name: '',
        component_name: '',
        updated_description: '',
    });

    useEffect(() => {
        if (apps && apps.length > 0) {
            const appIdFromQuery = searchParams.get('app_id');
            const defaultAppId = resolveDefaultAppId(apps, appIdFromQuery);
            setAppId(defaultAppId);
            loadComponents(defaultAppId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps, searchParams]);

    const loadComponents = async (targetAppId) => {
        if (!targetAppId || targetAppId.trim() === '') return;
        setLoading(true);
        setError('');
        try {
            const data = await componentsService.getAll(targetAppId.trim());
            setComponents(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load components');
        } finally {
            setLoading(false);
        }
    };

    const handleAppChange = (e) => {
        const selectedAppId = e.target.value;
        setAppId(selectedAppId);
        setSelectedAppId(selectedAppId);
        if (selectedAppId) loadComponents(selectedAppId);
    };

    const handleCreateComponent = async (e) => {
        e.preventDefault();
        if (!appId) return setError('App ID is required');
        setError('');
        try {
            await componentsService.addComponent(appId, formData);
            setSuccessMessage('Component created successfully!');
            setShowCreateModal(false);
            resetForm();
            loadComponents(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create component');
        }
    };

    const handleUpdateName = async (e) => {
        e.preventDefault();
        if (!appId || !selectedComponent) return;
        setError('');
        try {
            await componentsService.updateName(appId, {
                old_component_name: selectedComponent.component_name,
                new_component_name: updateFormData.new_component_name,
            });
            setSuccessMessage('Component name updated successfully!');
            setShowUpdateModal(false);
            setSelectedComponent(null);
            resetUpdateForm();
            loadComponents(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update name');
        }
    };

    const handleUpdateDescription = async (e) => {
        e.preventDefault();
        if (!appId || !selectedComponent) return;
        setError('');
        try {
            await componentsService.updateDescription(appId, {
                component_name: selectedComponent.component_name,
                updated_description: updateFormData.updated_description,
            });
            setSuccessMessage('Component description updated successfully!');
            setShowUpdateModal(false);
            setSelectedComponent(null);
            resetUpdateForm();
            loadComponents(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update description');
        }
    };

    const handleDeleteComponent = async () => {
        if (!appId || !selectedComponent) return;
        if (!window.confirm(`Are you sure you want to delete component "${selectedComponent.component_name}"?`)) return;
        setError('');
        try {
            await componentsService.removeComponent(appId, { component_name: selectedComponent.component_name });
            setSuccessMessage('Component deleted successfully!');
            setShowDeleteModal(false);
            setSelectedComponent(null);
            loadComponents(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to delete component');
        }
    };

    const resetForm = () => {
        setFormData({ component_name: '', component_description: '' });
    };

    const resetUpdateForm = () => {
        setUpdateFormData({
            old_component_name: '',
            new_component_name: '',
            component_name: '',
            updated_description: '',
        });
    };

    const openUpdateModal = (component) => {
        setSelectedComponent(component);
        setUpdateFormData({
            old_component_name: component.component_name,
            new_component_name: component.component_name,
            component_name: component.component_name,
            updated_description: component.component_description || '',
        });
        setShowUpdateModal(true);
    };

    const openDeleteModal = (component) => {
        setSelectedComponent(component);
        setShowDeleteModal(true);
    };

    // Mapping CRUD -------------------------------------------------------------

    const openComponentRoles = async (component) => {
        if (!appId) return;
        setSelectedComponent(component);
        setComponentRoles([]);
        setAllRoles([]);
        setLoadingComponentRoles(true);
        setShowComponentRolesModal(true);
        setMappingError('');
        setNewMapping({ rolename: '', access: '{"access": "readonly"}' });
        setEditingRoleMappingId(null);
        try {
            // Load Mappings
            const data = await componentsService.getComponentDetails(appId, component.component_name);
            const rawRoles = Array.isArray(data) && data[0]?.component_roles ? data[0].component_roles : [];
            setComponentRoles(rawRoles.filter((r) => r.role_id)); // Filter valid roles

            // Load Available Roles for Dropdown
            const rolesData = await rolesService.getAll(appId);
            // Flag roles grant eligibility only — never mapped to components.
            setAllRoles(splitRoles(rolesData).roles);
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to load details');
        } finally {
            setLoadingComponentRoles(false);
        }
    };

    const refreshComponentRoles = async () => {
        if (!selectedComponent) return;
        try {
            const data = await componentsService.getComponentDetails(appId, selectedComponent.component_name);
            const rawRoles = Array.isArray(data) && data[0]?.component_roles ? data[0].component_roles : [];
            setComponentRoles(rawRoles.filter((r) => r.role_id)); // Filter valid roles
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddRoleMapping = async (e) => {
        e.preventDefault();
        if (!newMapping.rolename) return setMappingError('Select a role');
        setMappingError('');
        try {
            await componentsService.addRoleMapping(appId, {
                rolename: newMapping.rolename,
                component_name: selectedComponent.component_name,
                role_component_access: JSON.parse(newMapping.access),
            });
            setNewMapping({ ...newMapping, rolename: '' });
            await refreshComponentRoles();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to add mapping');
        }
    };

    const handleDeleteRoleMapping = async (rolename) => {
        if (!window.confirm(`Unmap role "${rolename}" from ${selectedComponent.component_name}?`)) return;
        setMappingError('');
        try {
            await componentsService.removeRoleMapping(appId, {
                rolename: rolename,
                component_name: selectedComponent.component_name,
            });
            await refreshComponentRoles();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to remove mapping');
        }
    };

    const startEditingRole = (role) => {
        setEditingRoleMappingId(role.role_id);
        const accessVal = typeof role.accesses === 'string' ? role.accesses : JSON.stringify(role.accesses || {});
        setEditAccessValue(accessVal);
    };

    const handleUpdateRoleMapping = async (rolename) => {
        setMappingError('');
        try {
            await componentsService.updateRoleAccess(appId, {
                rolename: rolename,
                component_name: selectedComponent.component_name,
                updated_access: JSON.parse(editAccessValue),
            });
            setEditingRoleMappingId(null);
            await refreshComponentRoles();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to update access');
        }
    };

    // Helper to filter out already mapped roles
    const availableRoles = allRoles.filter((r) => !componentRoles.some((cr) => cr.role_name === r.role_name));

    return (
        <div className="space-y-4">
            <PageHeader
                title="Components"
                subtitle={`Manage components for ${appId ? `App: ${appId.substring(0, 8)}...` : 'selected app'}`}
                actions={
                    <div className="flex gap-2">
                        <select
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[200px]"
                            value={appId}
                            onChange={handleAppChange}
                            disabled={loadingApps}
                        >
                            <option value="">Select an App</option>
                            {apps.map((app) => (
                                <option key={app.app_id} value={app.app_id}>
                                    {app.app_name} {app.is_active ? '' : '(Disabled)'}
                                </option>
                            ))}
                        </select>
                        {appId && (
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4" /> Add Component
                            </button>
                        )}
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                    {successMessage}
                </div>
            )}

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : loading ? (
                <SkeletonList />
            ) : error ? (
                <ErrorState message={error} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">Select an App to view components.</p>
                </div>
            ) : components.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">No components found for the selected app.</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {components.map((c) => (
                        <div
                            key={c.component_id}
                            className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-sm hover:shadow-md transition space-y-2"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="h-9 w-9 rounded-lg bg-primary-50 text-primary-700 grid place-content-center shadow-inner">
                                        <Puzzle className="h-4 w-4" />
                                    </div>
                                    <div className="text-base font-semibold text-slate-900">{c.component_name}</div>
                                </div>
                                <span
                                    className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                                        c.is_active
                                            ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                                            : 'text-amber-700 bg-amber-50 border-amber-100'
                                    }`}
                                >
                                    {c.is_active ? 'Active' : 'Disabled'}
                                </span>
                            </div>
                            <div className="text-sm text-slate-600 break-all">{c.component_description || '—'}</div>
                            <div className="flex gap-2 pt-2 border-t border-slate-100">
                                <button
                                    onClick={() => openComponentRoles(c)}
                                    className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center justify-center gap-1"
                                >
                                    <Eye className="h-3 w-3" /> View Roles
                                </button>
                                <button
                                    onClick={() => openUpdateModal(c)}
                                    className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 flex items-center justify-center gap-1"
                                >
                                    <Edit2 className="h-3 w-3" /> Edit
                                </button>
                                <button
                                    onClick={() => openDeleteModal(c)}
                                    className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 flex items-center justify-center gap-1"
                                >
                                    <Trash2 className="h-3 w-3" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Component Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create New Component"
                size="md"
            >
                <form onSubmit={handleCreateComponent} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Component Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.component_name}
                            onChange={(e) => setFormData({ ...formData, component_name: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="Component Name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Component Description *</label>
                        <textarea
                            required
                            rows={4}
                            value={formData.component_description}
                            onChange={(e) => setFormData({ ...formData, component_description: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="Component description"
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Create Component
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Update Component Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => {
                    setShowUpdateModal(false);
                    setSelectedComponent(null);
                }}
                title={`Update ${selectedComponent?.component_name || 'Component'}`}
                size="md"
            >
                <div className="space-y-6">
                    <form onSubmit={handleUpdateName} className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-900">Update Name</h3>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                New Component Name *
                            </label>
                            <input
                                type="text"
                                required
                                value={updateFormData.new_component_name}
                                onChange={(e) =>
                                    setUpdateFormData({ ...updateFormData, new_component_name: e.target.value })
                                }
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Update Name
                        </button>
                    </form>

                    <div className="border-t border-slate-200 pt-4">
                        <form onSubmit={handleUpdateDescription} className="space-y-4">
                            <h3 className="text-sm font-semibold text-slate-900">Update Description</h3>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    New Description *
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={updateFormData.updated_description}
                                    onChange={(e) =>
                                        setUpdateFormData({ ...updateFormData, updated_description: e.target.value })
                                    }
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                            >
                                Update Description
                            </button>
                        </form>
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="pt-4 border-t border-slate-200">
                        <button
                            onClick={() => {
                                setShowUpdateModal(false);
                                setSelectedComponent(null);
                            }}
                            className="w-full rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Delete Component Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedComponent(null);
                }}
                title={`Delete ${selectedComponent?.component_name || 'Component'}`}
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete component <strong>{selectedComponent?.component_name}</strong>?
                        This action cannot be undone.
                    </p>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            onClick={handleDeleteComponent}
                            className="flex-1 rounded-lg bg-red-600 text-white font-medium py-2.5 hover:bg-red-700"
                        >
                            Delete
                        </button>
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedComponent(null);
                            }}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Component Roles Modal */}
            <Modal
                isOpen={showComponentRolesModal}
                onClose={() => {
                    setShowComponentRolesModal(false);
                    setSelectedComponent(null);
                    setComponentRoles([]);
                    setMappingError('');
                }}
                title={selectedComponent ? `Roles for ${selectedComponent.component_name}` : 'Roles'}
                size="lg"
            >
                <div className="space-y-6">
                    {/* Add New Mapping Form */}
                    <form
                        onSubmit={handleAddRoleMapping}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3"
                    >
                        <h4 className="text-sm font-semibold text-slate-800">Assign New Role</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <select
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                value={newMapping.rolename}
                                onChange={(e) => setNewMapping({ ...newMapping, rolename: e.target.value })}
                                required
                            >
                                <option value="">Select Role...</option>
                                {availableRoles.map((r) => (
                                    <option key={r.role_id} value={r.role_name}>
                                        {r.role_name}
                                    </option>
                                ))}
                            </select>
                            <input
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
                                value={newMapping.access}
                                onChange={(e) => setNewMapping({ ...newMapping, access: e.target.value })}
                                placeholder="Access JSON"
                                required
                            />
                            <button
                                type="submit"
                                className="rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium hover:bg-primary-700"
                            >
                                Assign Role
                            </button>
                        </div>
                    </form>

                    {mappingError && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {mappingError}
                        </div>
                    )}

                    {loadingComponentRoles ? (
                        <SkeletonList />
                    ) : componentRoles.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl border border-slate-100 border-dashed">
                            No roles assigned to this component.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                            <table className="min-w-full text-sm text-left text-slate-700">
                                <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                                    <tr>
                                        <th className="px-4 py-3">Role</th>
                                        <th className="px-4 py-3">Access</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {componentRoles.map((role) => (
                                        <tr key={role.role_id} className="hover:bg-slate-50/50">
                                            <td className="px-4 py-3 font-semibold text-slate-900 align-top">
                                                {role.role_name || 'Unknown Role'}
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                {editingRoleMappingId === role.role_id ? (
                                                    <div className="space-y-2">
                                                        <textarea
                                                            className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-mono"
                                                            rows={2}
                                                            value={editAccessValue}
                                                            onChange={(e) => setEditAccessValue(e.target.value)}
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleUpdateRoleMapping(role.role_name)}
                                                                className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200 flex items-center gap-1"
                                                            >
                                                                <Save size={12} /> Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingRoleMappingId(null)}
                                                                className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-slate-200"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <code className="text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200 block text-slate-600 font-mono break-all max-w-[300px]">
                                                        {typeof role.accesses === 'string'
                                                            ? role.accesses
                                                            : JSON.stringify(role.accesses || {})}
                                                    </code>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right align-top">
                                                <div className="flex justify-end gap-2">
                                                    {editingRoleMappingId !== role.role_id && (
                                                        <>
                                                            <button
                                                                onClick={() => startEditingRole(role)}
                                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Edit Access"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteRoleMapping(role.role_name)}
                                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Remove Mapping"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ComponentsPage;
